import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, Typography, Paper, Button, Card, CardContent, Grid, CircularProgress, Divider, Alert } from '@mui/material';
import {  ArrowBack, CreditCard, Badge, MonetizationOn } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { stripeCoursePaymentService } from './stripeCoursePaymentService';
import { cashCoursePaymentService } from './cashCoursePaymentService';
import { paypalPaymentService } from '../membership-workflow/paypalPaymentService';
import CashCoursePaymentModal from './CashCoursePaymentModal';
import getConfig from '../../config/app-config';
import dayjs from 'dayjs';

const CourseEnrollmentCheckout = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCashModal, setShowCashModal] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [isFreeEnrollment, setIsFreeEnrollment] = useState(false);

  // Get configuration
  const config = getConfig();

  // Get course data from location state and save it locally
  const getCourseData = () => {
    return location.state?.courseData || null;
  };

  // Initialize course data from location state
  useEffect(() => {
    const data = getCourseData();
    if (data) {
      setCourseData(data);
      // Check if this is a free enrollment (all pricing fields are 0)
      const checkIfFree = data.pricing && 
        data.pricing.monthly === 0 && 
        data.pricing.annual3 === 0 && 
        data.pricing.annual1 === 0;
      setIsFreeEnrollment(checkIfFree);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleStartPayment = async () => {
    setLoading(true);
    setError('');

    const result = await stripeCoursePaymentService.createPaymentSession(courseData);
    if (result.success) {
      stripeCoursePaymentService.redirectToCheckout(result.checkoutUrl);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleCashPaymentClick = () => {
    setShowCashModal(true);
  };

  const handleCashPaymentConfirm = async () => {
    setShowCashModal(false);
    setLoading(true);
    setError('');
    const result = await cashCoursePaymentService.createCashEnrollment(user, courseData);
    if (result.success) {
      cashCoursePaymentService.updateUserWithEnrollment(user, result.enrollment, updateUser);
      navigate(`/course-enrollment-callback?payment_method=cash&payment_status=success&amount=${courseData.amount}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleFreeEnrollment = async () => {
    setLoading(true);
    setError('');
    // Create a modified courseData with amount 0 for free enrollment
    const freeCourseData = { ...courseData, amount: 0 };
    const result = await cashCoursePaymentService.createCashEnrollment(user, freeCourseData);
    if (result.success) {
      cashCoursePaymentService.updateUserWithEnrollment(user, result.enrollment, updateUser);
      navigate(`/course-enrollment-callback?payment_method=cash&payment_status=success&amount=0`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleCashPaymentCancel = () => {
    setShowCashModal(false);
  };

  const handlePayPalRedirect = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await paypalPaymentService.createPayPalCourseOrder(courseData);
      
      if (result.success) {
        // Redirect to PayPal
        await paypalPaymentService.redirectToPayPal(result.approvalUrl);
      } else {
        console.error('PayPal course order creation failed:', result.error);
        setError(`Errore nella creazione dell'ordine PayPal: ${result.error}`);
      }
    } catch (error) {
      console.error('Error in handlePayPalRedirect:', error);
      setError(`Errore nella creazione dell'ordine PayPal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/courses');
  };

  // Mostra loader finché courseData non è disponibile
  if (!courseData) {
    return (
      <Container maxWidth='md'>
        <Box sx={{ py: 8, textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }}/>
          <Typography variant='h6'>Caricamento dati corso...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CashCoursePaymentModal
        open={showCashModal}
        onConfirm={handleCashPaymentConfirm}
        onCancel={handleCashPaymentCancel}
      />
      <Container maxWidth='md'>
        <Box sx={{ py: 4, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexShrink: 0 }}>
            <Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mr: 2 }}>
              Torna ai Corsi
            </Button>
           
          </Box>

          <Paper elevation={3} sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant='h5' gutterBottom sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Badge sx={{ mr: 1 }} />
              {courseData.name}
            </Typography>
            
            <Divider sx={{ my: 3, flexShrink: 0 }} />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Grid container spacing={3} sx={{ mb: 3, flexShrink: 0 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant='h4' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* <CalendarToday sx={{ mr: 1 }} /> */}
                        Date Corso
                      </Typography>
                      <Typography variant='body1' sx={{ mb: 1 }}>
                        <strong>Inizio corso:</strong> {dayjs(courseData.courseStartDate).format('DD MMM YYYY')}
                      </Typography>
                      <Typography variant='body1' sx={{ mb: 1 }}>
                        <strong>Fine corso:</strong> {courseData.courseEndDate ? dayjs(courseData.courseEndDate).format('DD MMM YYYY') : 'N/A'}
                      </Typography>
                      {courseData.enrollmentStartDate && (
                        <Typography variant='body1' sx={{ mb: 1 }}>
                          <strong>Iscrizione valida da:</strong> {dayjs(courseData.enrollmentStartDate).format('MMM YYYY')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant='h4' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* <Euro sx={{ mr: 1 }} /> */}
                        {isFreeEnrollment ? 'Costo' : 'Costo'}
                      </Typography>
                      {isFreeEnrollment ? (
                        <Box>
                          <Typography variant='h4' color='success.main' sx={{ fontWeight: 'bold' }}>
                            Iscrizione Gratuita
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Nessun costo richiesto
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant='h4' color='primary.main' sx={{ fontWeight: 'bold' }}>
                            {courseData.amount} euro
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Pagamento sicuro
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, mb: 3, flexShrink: 0 }}>
                <Typography variant='h6' gutterBottom>
                  Cosa include il corso:
                </Typography>
                <Box component='ul' sx={{ pl: 2 }}>
                  <Typography component='li' variant='body2'>
                    Accesso alle lezioni e materiali del corso
                  </Typography>
                  <Typography component='li' variant='body2'>
                    Supporto da parte dei docenti
                  </Typography>
                  <Typography component='li' variant='body2'>
                    Attestato di partecipazione (se previsto)
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {isFreeEnrollment ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  flexShrink: 0 
                }}>
                  <Button 
                    variant='contained' 
                    size='large' 
                    startIcon={<Badge />} 
                    onClick={handleFreeEnrollment} 
                    disabled={loading} 
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      minWidth: 200
                    }}
                  >
                    {loading ? <CircularProgress size={24} color='inherit' /> : 'Iscriviti'}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2, 
                  flexShrink: 0 
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center', 
                    gap: 2,
                    width: '100%'
                  }}>
                    {config.USE_STRIPE && (
                      <Button 
                      variant='contained' 
                      size='large' 
                      startIcon={<CreditCard />} 
                      onClick={handleStartPayment} 
                      disabled={loading} 
                      sx={{ 
                        px: 4, 
                        py: 1.5, 
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 200 }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga con Carta'}
                    </Button>
                    )}
                    <Button 
                      variant='contained' 
                      size='large' 
                      startIcon={<MonetizationOn />} 
                      onClick={handleCashPaymentClick} 
                      disabled={loading} 
                      sx={{ 
                        px: 4, 
                        py: 1.5, 
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 200 }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga in Contanti'}
                    </Button>
                  </Box>
                  
                  {/* Mostra PayPal solo se abilitato nella configurazione */}
                  {config.USE_PAYPAL && (
                    <Button 
                      variant='contained' 
                      size='large' 
                      onClick={handlePayPalRedirect} 
                      disabled={loading} 
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 200
                      }}
                    >
                      {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga con PayPal'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default CourseEnrollmentCheckout; 